import React from 'react';
import { Label, TextInput, Textarea, Select, Checkbox, Radio, FileInput, ToggleSwitch, RangeSlider } from 'flowbite-react';

// Form Field Wrapper
export const FormField: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    {children}
  </div>
);

// Enhanced Text Input
interface TextFieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  helperText?: string;
  icon?: React.FC<any>;
  disabled?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
  error,
  helperText,
  icon: Icon,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label htmlFor={id} color={error ? 'failure' : undefined}>{label}</Label>
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <TextInput
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        color={error ? 'failure' : undefined}
        icon={Icon}
        disabled={disabled}
      />
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </FormField>
  );
};

// Enhanced Text Area
interface TextAreaFieldProps {
  id: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  helperText?: string;
  rows?: number;
  disabled?: boolean;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  error,
  helperText,
  rows = 4,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label htmlFor={id} color={error ? 'failure' : undefined}>{label}</Label>
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <Textarea
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        color={error ? 'failure' : undefined}
        rows={rows}
        disabled={disabled}
      />
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </FormField>
  );
};

// Enhanced Select Field
interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  error,
  helperText,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label htmlFor={id} color={error ? 'failure' : undefined}>{label}</Label>
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <Select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        color={error ? 'failure' : undefined}
        disabled={disabled}
      >
        <option value="">Choose...</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </FormField>
  );
};

// Enhanced Checkbox Field
interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helperText?: string;
  disabled?: boolean;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  id,
  label,
  checked,
  onChange,
  helperText,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <Label htmlFor={id} disabled={disabled}>
          {label}
        </Label>
      </div>
      {helperText && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </FormField>
  );
};

// Enhanced Radio Group
interface RadioGroupProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label>{label}</Label>
      </div>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <Radio
              id={`${name}-${option.value}`}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            />
            <Label htmlFor={`${name}-${option.value}`} disabled={disabled}>
              {option.label}
            </Label>
          </div>
        ))}
      </div>
    </FormField>
  );
};

// Enhanced Toggle Switch
interface ToggleFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helperText?: string;
  disabled?: boolean;
}

export const ToggleField: React.FC<ToggleFieldProps> = ({
  id,
  label,
  checked,
  onChange,
  helperText,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="flex items-center gap-3">
        <ToggleSwitch
          id={id}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
        <Label htmlFor={id} disabled={disabled}>
          {label}
        </Label>
      </div>
      {helperText && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </FormField>
  );
};

// Enhanced File Input
interface FileFieldProps {
  id: string;
  label: string;
  onChange: (file: File | null) => void;
  helperText?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
}

export const FileField: React.FC<FileFieldProps> = ({
  id,
  label,
  onChange,
  helperText,
  accept,
  multiple = false,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label htmlFor={id}>{label}</Label>
      </div>
      <FileInput
        id={id}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
      />
      {helperText && (
        <p className="mt-2 text-sm text-gray-500">
          {helperText}
        </p>
      )}
    </FormField>
  );
};

// Range Slider Field
interface RangeFieldProps {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  helperText?: string;
  disabled?: boolean;
}

export const RangeField: React.FC<RangeFieldProps> = ({
  id,
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  helperText,
  disabled = false
}) => {
  return (
    <FormField>
      <div className="mb-2 block">
        <Label htmlFor={id}>{`${label}: ${value}`}</Label>
      </div>
      <RangeSlider
        id={id}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
      />
      {helperText && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </FormField>
  );
};

// Pre-configured form fields for common use cases
export const EmailField: React.FC<Omit<TextFieldProps, 'type' | 'icon'>> = (props) => (
  <TextField {...props} type="email" />
);

export const PasswordField: React.FC<Omit<TextFieldProps, 'type' | 'icon'>> = (props) => (
  <TextField {...props} type="password" />
);

export const UsernameField: React.FC<Omit<TextFieldProps, 'type' | 'icon'>> = (props) => (
  <TextField {...props} type="text" />
);

export const DateField: React.FC<Omit<TextFieldProps, 'type' | 'icon'>> = (props) => (
  <TextField {...props} type="date" />
);

export const TimeField: React.FC<Omit<TextFieldProps, 'type' | 'icon'>> = (props) => (
  <TextField {...props} type="time" />
);